import { useTranslation } from 'react-i18next';
import { CheckCircle2, Clock4 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { caseStatusBadgeColor, type RxCaseStatus } from '@/lib/api/rx';

const STATUS_KEY: Record<RxCaseStatus, string> = {
  requested: 'cases.status.requested',
  submitted: 'cases.status.submitted',
  in_review: 'cases.status.inReview',
  approved: 'cases.status.approved',
  shipped: 'cases.status.shipped',
  delivered: 'cases.status.delivered',
  declined: 'cases.status.declined',
  cancelled: 'cases.status.cancelled',
  error: 'cases.status.error',
};

export function RxCaseStatusBadge({ status }: { status: RxCaseStatus }) {
  const { t } = useTranslation('rx');
  return (
    <Badge variant="outline" className={`${caseStatusBadgeColor(status)} font-medium`}>
      {t(STATUS_KEY[status])}
    </Badge>
  );
}

/**
 * "Rx payments: setting up / ready". A status, never a step: the merchant has
 * nothing to do while Tagada opens the brand's payment account.
 */
export function RxPaymentsStatusBadge({
  payments,
  partnerBlocked = false,
}: {
  payments: 'setting_up' | 'ready';
  partnerBlocked?: boolean;
}) {
  const { t } = useTranslation('rx');
  const ready = payments === 'ready';
  const hint = ready
    ? t('onboardingJourney.payments.readyHint')
    : partnerBlocked
      ? t('onboardingJourney.payments.partnerHint')
      : t('onboardingJourney.payments.settingUpHint');
  return (
    <Badge
      variant="outline"
      title={hint}
      className={
        ready
          ? 'gap-1 border-emerald-200 bg-emerald-50 font-medium text-emerald-700'
          : 'gap-1 border-amber-200 bg-amber-50 font-medium text-amber-700'
      }
    >
      {ready ? <CheckCircle2 className="h-3 w-3" /> : <Clock4 className="h-3 w-3" />}
      {t('onboardingJourney.payments.label')}:{' '}
      {ready ? t('onboardingJourney.payments.ready') : t('onboardingJourney.payments.settingUp')}
    </Badge>
  );
}
